import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/base/Toast';
import { markUserLoginTime } from '@/components/feature/AuthGuard';

const STORAGE_KEY = 'register_form_draft';

// 将手机号映射为内部登录邮箱（Supabase 邮箱+密码模式的兼容方案）
const toAuthEmail = (phone: string) => `u${phone}@jinan-gold.com`;

export default function RegisterPage() {
  const navigate = useNavigate();
  const { t } = useTranslation(['common']);
  const toast = useToast();

  const savedDraft = (() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();

  const [formData, setFormData] = useState(() => savedDraft || {
    phone: '',
    password: '',
    confirmPassword: '',
    agree: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 保存表单草稿到 sessionStorage
  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
  }, [formData]);

  // 注册成功或离开页面时可选清理；这里保持：只在成功注册后清理
  const clearDraft = () => sessionStorage.removeItem(STORAGE_KEY);

  // 注册成功后清空该用户的预置测试数据
  const clearUserTestData = async () => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user?.id;
      if (!userId) return;

      console.log('[Register] 开始清理用户默认数据, userId:', userId);

      // 通过 Edge Function 用 service_role 权限清理，绕过 RLS 限制
      const { data, error } = await supabase.functions.invoke('clear-user-data', {
        body: { user_id: userId },
      });

      if (error) {
        console.error('[Register] Edge Function 调用失败:', error);
        return;
      }

      console.log('[Register] Edge Function 返回:', data);

      // 兜底：本地也尝试清理一次（若 Edge Function 不可用时的降级方案）
      const fallbackTasks = [
        supabase.from('profiles').upsert({ id: userId, order_count: 0, processing_count: 0, holding_gold: 0, asset_value: 0, balance: 0, verified: false }, { onConflict: 'id' }),
        supabase.from('account_summary').upsert({ user_id: userId, available_margin: 0, used_margin: 0, long_weight: 0, short_weight: 0, net_weight: 0, floating_profit: 0 }, { onConflict: 'user_id' }),
        supabase.from('orders').delete().eq('user_id', userId),
        supabase.from('gold_assets').delete().eq('user_id', userId),
        supabase.from('messages').delete().eq('user_id', userId),
        supabase.from('addresses').delete().eq('user_id', userId),
        supabase.from('bank_cards').delete().eq('user_id', userId),
        supabase.from('withdraw_records').delete().eq('user_id', userId),
        supabase.from('dispose_records').delete().eq('user_id', userId),
        supabase.from('perform_records').delete().eq('user_id', userId),
        supabase.from('payments').delete().eq('user_id', userId),
        supabase.from('pickup_records').delete().eq('user_id', userId),
        supabase.from('deposits').delete().eq('user_id', userId),
      ];
      await Promise.allSettled(fallbackTasks);

      console.log('[Register] 用户默认数据清理完成');
    } catch (err) {
      console.error('[Register] clearUserTestData 异常:', err);
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.phone.trim()) {
      newErrors.phone = t('common:errorPhoneEmpty');
    } else if (!/^1[3-9]\d{9}$/.test(formData.phone.trim())) {
      newErrors.phone = t('common:errorPhoneFormat');
    }
    if (!formData.password.trim()) {
      newErrors.password = t('common:errorPasswordEmpty');
    } else if (formData.password.length < 6) {
      newErrors.password = t('common:errorPasswordLength');
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('common:errorPasswordMismatch');
    }
    if (!formData.agree) {
      newErrors.agree = t('common:errorAgreeTerms');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    setFormError('');

    const phone = formData.phone.trim();
    const email = toAuthEmail(phone);

    console.log('[Register] attempting signUp with email:', email);

    // 先尝试直接登录：如果用户已存在，直接登录成功，避免触发注册限流
    const { data: loginData, error: loginError } = await supabase.auth.signInWithPassword({
      email,
      password: formData.password,
    });

    if (loginData.session) {
      console.log('[Register] 用户已存在，直接登录成功');
      markUserLoginTime();
      clearDraft();
      await clearUserTestData();
      toast.showToast(t('common:loginSuccess'));
      navigate('/home', { replace: true });
      return;
    }

    // 登录失败（用户不存在）才走注册
    const { data, error } = await supabase.auth.signUp({
      email,
      password: formData.password,
      options: { data: { phone } },
    });

    console.log('[Register] signUp result:', { data, error });

    if (error) {
      setIsSubmitting(false);
      const msg = error.message || '';
      const code = (error as any).code || '';
      const status = (error as any).status || '';
      console.error('[Register] signUp error:', { msg, code, status, error });

      if (msg.toLowerCase().includes('already registered') || msg.toLowerCase().includes('already exists')) {
        setFormError(t('common:errorAccountExists'));
      } else if (code === 'over_email_send_rate_limit' || msg.toLowerCase().includes('rate limit')) {
        setFormError('注册太频繁，请稍后重试。建议去 Supabase Dashboard 关闭邮箱验证(Confirm email)以避免此问题。');
      } else {
        setFormError(t('common:errorRegisterFailed', { message: msg, code, status }));
      }
      return;
    }

    if (data.session) {
      markUserLoginTime();
      clearDraft();
      await clearUserTestData();
      toast.showToast(t('common:registerSuccess'));
      navigate('/home', { replace: true });
      return;
    }

    // session 为空但无 error：可能邮箱验证开启，尝试直接登录
    const { data: loginData2, error: loginError2 } = await supabase.auth.signInWithPassword({
      email,
      password: formData.password,
    });

    setIsSubmitting(false);

    if (loginError2) {
      console.error('[Register] fallback login error:', loginError2);
      setFormError(t('common:errorRegisterSuccessNoLogin'));
      return;
    }

    if (loginData2.session) {
      markUserLoginTime();
      clearDraft();
      await clearUserTestData();
      toast.showToast(t('common:registerSuccess'));
      navigate('/home', { replace: true });
    } else {
      setFormError(t('common:errorRegisterSuccessNoLogin'));
    }
  };

  return (
    <div className="min-h-screen w-full bg-brown-50 flex flex-col items-center relative">
      {/* Header */}
      <div className="w-full bg-brown-950 px-4 py-3 flex items-center sticky top-0 z-10">
        <button
          onClick={() => navigate('/login')}
          className="w-8 h-8 flex items-center justify-center text-gold-400 cursor-pointer"
        >
          <i className="ri-arrow-left-line text-lg"></i>
        </button>
        <h1 className="text-gold-400 text-lg font-bold flex-1 text-center pr-8">{t('common:register')}</h1>
      </div>

      <div className="w-full max-w-[420px] px-4 pt-8 pb-12 md:pt-10 md:pb-16">
        <div className="bg-white rounded-2xl border border-brown-100 px-6 py-8 md:px-8 md:py-10">
          {/* Brand */}
          <div className="flex justify-center">
            <img
              src="https://public.readdy.ai/ai/img_res/3dc270e4-5b61-4676-ac46-8193427c09c6.png"
              alt="CJJY"
              className="h-24 w-auto object-contain max-w-full"
            />
          </div>

          <h2 className="text-2xl font-bold text-brown-900 mt-6 mb-2 text-center">{t('common:welcomeRegister')}</h2>
          <p className="text-brown-500 text-sm mb-8 text-center">{t('common:registerSubtitle')}</p>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
            {/* Phone */}
            <div>
              <div className="flex items-center border border-brown-200 rounded-lg px-4 py-3 bg-white transition-all duration-200 hover:border-brown-300 focus-within:border-gold-400 focus-within:ring-2 focus-within:ring-gold-400/20">
                <input
                  type="tel"
                  inputMode="numeric"
                  maxLength={11}
                  value={formData.phone}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '');
                    setFormData({ ...formData, phone: val });
                    if (errors.phone) setErrors({ ...errors, phone: '' });
                  }}
                  placeholder={t('common:placeholderPhone')}
                  className="flex-1 text-sm text-brown-900 placeholder:text-brown-400 bg-transparent outline-none"
                />
              </div>
              {errors.phone && <p className="text-xs text-red-500 mt-1 ml-1">{errors.phone}</p>}
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center border border-brown-200 rounded-lg px-4 py-3 bg-white transition-all duration-200 hover:border-brown-300 focus-within:border-gold-400 focus-within:ring-2 focus-within:ring-gold-400/20">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => {
                    setFormData({ ...formData, password: e.target.value });
                    if (errors.password) setErrors({ ...errors, password: '' });
                  }}
                  placeholder={t('common:placeholderPassword')}
                  className="flex-1 text-sm text-brown-900 placeholder:text-brown-400 bg-transparent outline-none"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="ml-2 w-5 h-5 flex items-center justify-center text-brown-400 cursor-pointer"
                >
                  <i className={`${showPassword ? 'ri-eye-line' : 'ri-eye-off-line'} text-lg`}></i>
                </button>
              </div>
              {errors.password && <p className="text-xs text-red-500 mt-1 ml-1">{errors.password}</p>}
            </div>

            {/* Confirm Password */}
            <div>
              <div className="flex items-center border border-brown-200 rounded-lg px-4 py-3 bg-white transition-all duration-200 hover:border-brown-300 focus-within:border-gold-400 focus-within:ring-2 focus-within:ring-gold-400/20">
                <input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => {
                    setFormData({ ...formData, confirmPassword: e.target.value });
                    if (errors.confirmPassword) setErrors({ ...errors, confirmPassword: '' });
                  }}
                  placeholder={t('common:confirmPassword')}
                  className="w-full text-sm text-brown-900 placeholder:text-brown-400 bg-transparent outline-none"
                />
              </div>
              {errors.confirmPassword && (
                <p className="text-xs text-red-500 mt-1 ml-1">{errors.confirmPassword}</p>
              )}
            </div>

            {/* Agreement */}
            <div>
              <label className="flex items-start gap-2 cursor-pointer">
                <div
                  onClick={() => {
                    setFormData({ ...formData, agree: !formData.agree });
                    if (errors.agree) setErrors({ ...errors, agree: '' });
                  }}
                  className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 mt-0.5 cursor-pointer ${
                    formData.agree
                      ? 'bg-gold-500 border-gold-500'
                      : 'border-brown-300'
                  }`}
                >
                  {formData.agree && (
                    <span className="w-3 h-3 flex items-center justify-center text-brown-950">
                      <i className="ri-check-line text-xs"></i>
                    </span>
                  )}
                </div>
                <span className="text-brown-500 text-xs leading-relaxed">
                  {t('common:agreeTerms')}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/terms');
                    }}
                    className="text-gold-600 cursor-pointer whitespace-nowrap"
                  >
                    {t('common:userAgreement')}
                  </button>
                  {'和'}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/privacy');
                    }}
                    className="text-gold-600 cursor-pointer whitespace-nowrap"
                  >
                    {t('common:privacyPolicyDoc')}
                  </button>
                </span>
              </label>
              {errors.agree && <p className="text-xs text-red-500 mt-1 ml-6">{errors.agree}</p>}
            </div>

            {/* Form Error */}
            {formError && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-100 rounded-lg px-3 py-2.5">
                <span className="w-4 h-4 flex items-center justify-center text-red-500 flex-shrink-0">
                  <i className="ri-error-warning-line text-sm"></i>
                </span>
                <span className="text-xs text-red-600">{formError}</span>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 rounded-lg bg-gold-500 hover:bg-gold-600 text-brown-950 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap mt-2"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-brown-950/30 border-t-brown-950 rounded-full animate-spin"></span>
                  {t('common:loading')}
                </span>
              ) : (
                t('common:register')
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <span className="text-brown-500 text-sm">{t('common:hasAccount')}</span>
            <button
              onClick={() => navigate('/login')}
              className="ml-1.5 text-sm text-gold-600 font-medium cursor-pointer whitespace-nowrap"
            >
              {t('common:loginNow')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}